// src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const LEVELS = ["basic", "plus", "pro", "vip"];
const LOCALE = "en-US"; // ✅ stable for prerender + client
const LANDING_PATH = "/top"; // ✅ IMPORTANT: use /top (not /) to avoid redirect back to dashboard

function levelRank(level) {
  const i = LEVELS.indexOf(String(level || "").toLowerCase());
  return i === -1 ? 0 : i + 1;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function money(n) {
  const x = Number(n) || 0;
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(x);
  } catch {
    // ✅ stable fallback
    return `$${x.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }
}

/** Simple stacked bar diagram (no extra libs) */
function StackedBar({ items }) {
  const total = items.reduce((s, x) => s + (Number(x.value) || 0), 0) || 1;

  return (
    <div className="w-full">
      <div className="h-4 w-full rounded-full overflow-hidden border border-slate-700 bg-slate-950/50 flex">
        {items.map((it) => {
          const v = Number(it.value) || 0;
          const pct = (v / total) * 100;
          return (
            <div
              key={it.key}
              title={`${it.label}: ${money(v)} (${pct.toFixed(0)}%)`}
              className={it.className}
              style={{ width: `${pct}%` }}
            />
          );
        })}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {items.map((it) => (
          <div
            key={it.key}
            className="rounded-2xl border border-slate-800 bg-slate-950/40 p-3"
          >
            <div className="flex items-center gap-2">
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${it.dot}`} />
              <div className="text-xs text-slate-300">{it.label}</div>
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-50">
              {money(it.value)}
            </div>
            <div className="text-[11px] text-slate-400">{it.hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mini projection line chart using SVG */
function ProjectionLine({ points }) {
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x * 100} ${100 - p.y * 100}`)
    .join(" ");

  return (
    <div className="w-full rounded-2xl border border-slate-800 bg-slate-950/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-slate-200">12-month projection</div>
        <div className="text-[11px] text-slate-400">Cumulative (simple model)</div>
      </div>
      <svg viewBox="0 0 100 100" className="mt-2 h-20 w-full">
        <path
          d="M 0 100 L 100 100"
          stroke="rgba(148,163,184,.35)"
          strokeWidth="1"
          fill="none"
        />
        <path d={d} stroke="rgba(16,185,129,.95)" strokeWidth="3" fill="none" />
        <path
          d={d}
          stroke="rgba(16,185,129,.25)"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Basic dashboard data
  const [membershipLevel, setMembershipLevel] = useState("basic");

  // Totals shown on dashboard (these can later come from your DB tables)
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [donationsTotal, setDonationsTotal] = useState(0);
  const [spendingsTotal, setSpendingsTotal] = useState(0);

  // Lists (kept, even if empty)
  const [perks, setPerks] = useState([]);
  const [events, setEvents] = useState([]);
  const [recentFinancial, setRecentFinancial] = useState([]);
  const [recentSpendings, setRecentSpendings] = useState([]);

  // --- “Financial Snapshot” simulator controls ---
  const [whatIfMonthly, setWhatIfMonthly] = useState(50);
  const [donatePct, setDonatePct] = useState(50); // 0..100
  const [scenario, setScenario] = useState("donate"); // donate | spend | mixed

  // Redirect if not logged in
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  // Load membership plan (and your totals if you have them stored)
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const lvl =
        user?.user_metadata?.plan ||
        user?.user_metadata?.level ||
        user?.app_metadata?.plan ||
        "basic";

      setMembershipLevel(String(lvl || "basic").toLowerCase());

      setIncomeTotal(0);
      setDonationsTotal(0);
      setSpendingsTotal(0);

      setPerks([]);
      setEvents([]);
      setRecentFinancial([]);
      setRecentSpendings([]);
    };

    load();
  }, [user]);

  // Proper Sign Out
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();

      // extra safety: clear any cached supabase auth keys
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-"))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      console.error("Sign out error:", e);
    } finally {
      // hard redirect prevents stale state
      window.location.replace("/login");
    }
  };

  // Plan discounts for simulator (example)
  const planDiscount = useMemo(() => {
    const l = String(membershipLevel || "").toLowerCase();
    if (l === "vip") return 0.2;
    if (l === "pro") return 0.15;
    if (l === "plus") return 0.1;
    if (l === "basic") return 0.05;
    return 0;
  }, [membershipLevel]);

  // Simulator numbers
  const snapshot = useMemo(() => {
    const monthly = clamp(Number(whatIfMonthly) || 0, 0, 5000);
    const dPct = clamp(Number(donatePct) || 0, 0, 100) / 100;

    const impact = monthly * dPct;
    const remaining = monthly - impact;

    const savings = remaining * 0.7;
    const coaching = remaining * 0.2;
    const ops = remaining * 0.1;

    const assumedSpend =
      scenario === "spend" ? monthly : scenario === "mixed" ? monthly * 0.5 : 0;

    const discountSavings = assumedSpend * planDiscount;

    const monthBenefit = impact + savings + discountSavings;
    const yearBenefit = monthBenefit * 12;

    const maxY = Math.max(yearBenefit, 1);
    const points = Array.from({ length: 12 }).map((_, i) => {
      const cum = monthBenefit * (i + 1);
      return { x: i / 11, y: clamp(cum / maxY, 0, 1) };
    });

    return {
      impact,
      savings,
      coaching,
      ops,
      discountSavings,
      monthBenefit,
      yearBenefit,
      points,
    };
  }, [whatIfMonthly, donatePct, scenario, planDiscount]);

  const rank = levelRank(membershipLevel);
  const maxRank = LEVELS.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-slate-200">
          Loading…
        </div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
      {/* Background glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-sky-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 md:px-10 lg:px-16 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-50 truncate">
              Welcome, {user?.email || "Member"}
            </h1>
            <div className="mt-2 text-sm text-slate-300">
              Membership:{" "}
              <span className="text-emerald-300 font-semibold">
                {membershipLevel || "basic"}
              </span>{" "}
              <span className="text-slate-400">
                (rank {rank}/{maxRank})
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* ✅ FIXED HOME BUTTON */}
            <button
              onClick={() => navigate(LANDING_PATH, { replace: true })}
              className="rounded-xl border border-slate-700 bg-slate-900/40 px-4 py-2 text-sm text-slate-100 hover:border-emerald-400 transition-colors"
            >
              Home
            </button>

            <button
              onClick={handleSignOut}
              className="rounded-xl bg-emerald-500/20 border border-emerald-500/40 px-4 py-2 text-sm text-emerald-200 hover:bg-emerald-500/30 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Top cards */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="text-sm text-slate-300">Income</div>
            <div className="mt-2 text-2xl font-semibold text-slate-50">
              {money(incomeTotal)}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="text-sm text-slate-300">Donations</div>
            <div className="mt-2 text-2xl font-semibold text-slate-50">
              {money(donationsTotal)}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="text-sm text-slate-300">Spendings</div>
            <div className="mt-2 text-2xl font-semibold text-slate-50">
              {money(spendingsTotal)}
            </div>
          </div>
        </div>

        {/* Financial Snapshot */}
        <motion.section
          className="mt-6 rounded-3xl border border-slate-800 bg-slate-900/50 p-5 sm:p-7"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-sky-300">
                Financial snapshot (visual)
              </div>
              <h2 className="mt-2 text-xl sm:text-2xl font-semibold text-slate-50">
                Your financial situation with WOW50/50
              </h2>
              <p className="mt-2 text-sm text-slate-300 max-w-3xl">
                This diagram shows a simple “what-if” outlook. Slide the controls to preview how your
                numbers look if you donate more, spend more, or change your plan.
              </p>
              <div className="mt-2 text-[11px] text-slate-400">
                Current plan discount estimate:{" "}
                <span className="text-emerald-300 font-semibold">
                  {Math.round(planDiscount * 100)}%
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 w-full md:max-w-sm">
              <div className="text-xs font-semibold text-slate-200">What-if monthly amount</div>
              <div className="mt-1 text-2xl font-semibold text-emerald-300">
                {money(whatIfMonthly)}
              </div>
              <input
                type="range"
                min={0}
                max={5000}
                step={10}
                value={whatIfMonthly}
                onChange={(e) => setWhatIfMonthly(Number(e.target.value))}
                className="mt-3 w-full"
              />
              <div className="mt-2 text-[11px] text-slate-400">
                Increase this to preview “if you paid more”.
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-slate-200">Donate vs personal</div>
                <div className="text-xs font-semibold text-sky-300">{donatePct}% donate</div>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={donatePct}
                onChange={(e) => setDonatePct(Number(e.target.value))}
                className="mt-3 w-full"
              />

              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {[
                  { id: "donate", label: "Donate focus" },
                  { id: "spend", label: "Spend focus" },
                  { id: "mixed", label: "Mixed" },
                ].map((s) => (
                  <label
                    key={s.id}
                    className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm ${
                      scenario === s.id
                        ? "border-emerald-400/70 bg-emerald-400/10 text-emerald-100"
                        : "border-slate-800 bg-slate-950/30 text-slate-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="scenario"
                      value={s.id}
                      checked={scenario === s.id}
                      onChange={() => setScenario(s.id)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>

              <div className="mt-4">
                <StackedBar
                  items={[
                    {
                      key: "impact",
                      label: "Impact (donations)",
                      value: snapshot.impact,
                      className: "bg-emerald-500/80",
                      dot: "bg-emerald-400",
                      hint: "Supports people in need / aid tracks",
                    },
                    {
                      key: "savings",
                      label: "Savings / wellness",
                      value: snapshot.savings,
                      className: "bg-sky-500/70",
                      dot: "bg-sky-400",
                      hint: "Personal wellness & savings concept",
                    },
                    {
                      key: "ops",
                      label: "Coaching + ops",
                      value: snapshot.coaching + snapshot.ops,
                      className: "bg-indigo-500/60",
                      dot: "bg-indigo-400",
                      hint: "Education, compliance, platform support",
                    },
                  ]}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-sky-300">
                    Est. marketplace savings
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-50">
                    {money(snapshot.discountSavings)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">
                    Based on scenario spend + plan estimate.
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-sky-300">
                    Monthly benefit
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-emerald-300">
                    {money(snapshot.monthBenefit)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">Simple model output.</div>
                </div>

                <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-sky-300">
                    12-month total
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-50">
                    {money(snapshot.yearBenefit)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">Projection.</div>
                </div>
              </div>

              <ProjectionLine points={snapshot.points} />

              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-amber-200">
                  Disclaimer
                </div>
                <p className="mt-2 text-xs text-amber-100 leading-relaxed">
                  Contributions are <b>non-refundable</b>. After each contribution, you will receive
                  your year-end tax document between the <b>end of December</b> and the{" "}
                  <b>beginning of January</b> each year (ongoing). You can add{" "}
                  <b>dedications</b> each time you donate/contribute.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Perks */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-slate-100 mb-3">Perks</h3>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <div className="grid grid-cols-3 px-4 py-3 text-sm text-slate-300 border-b border-slate-800">
              <div>Title</div>
              <div>Level</div>
              <div>Details</div>
            </div>
            <div className="p-4 text-slate-300">{perks.length ? "Loaded perks…" : "No data yet."}</div>
          </div>
        </div>

        {/* Events */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-slate-100 mb-3">Events</h3>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <div className="grid grid-cols-3 px-4 py-3 text-sm text-slate-300 border-b border-slate-800">
              <div>Date</div>
              <div>Name</div>
              <div>Location</div>
            </div>
            <div className="p-4 text-slate-300">{events.length ? "Loaded events…" : "No data yet."}</div>
          </div>
        </div>

        {/* Recent Financial Activity */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-slate-100 mb-3">Recent Financial Activity</h3>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <div className="grid grid-cols-4 px-4 py-3 text-sm text-slate-300 border-b border-slate-800">
              <div>Date</div>
              <div>Type</div>
              <div>Amount</div>
              <div>Description</div>
            </div>
            <div className="p-4 text-slate-300">
              {recentFinancial.length ? "Loaded activity…" : "No data yet."}
            </div>
          </div>
        </div>

        {/* Recent Spendings */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-slate-100 mb-3">Recent Spendings</h3>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <div className="grid grid-cols-4 px-4 py-3 text-sm text-slate-300 border-b border-slate-800">
              <div>Date</div>
              <div>Category</div>
              <div>Amount</div>
              <div>Vendor</div>
            </div>
            <div className="p-4 text-slate-300">
              {recentSpendings.length ? "Loaded spendings…" : "No data yet."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}